import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function generateReferralCodeCandidate() {
  let seg = '';
  for (let i = 0; i < 6; i += 1) {
    seg += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return `MG-${seg}`;
}

/** Assign unique referral_code on current user's row */
export async function ensureMyReferralCode() {
  if (!isSupabaseConfigured) return null;
  const sb = getSupabase();
  if (!sb) return null;
  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user) return null;
  const { data: row } = await sb.from('users').select('id, referral_code').eq('auth_id', user.id).maybeSingle();
  if (!row?.id) return null;
  if (row.referral_code) return row.referral_code;
  for (let i = 0; i < 14; i += 1) {
    const code = generateReferralCodeCandidate();
    const { error } = await sb.from('users').update({ referral_code: code }).eq('id', row.id).is('referral_code', null);
    if (!error) return code;
    const { data: again } = await sb.from('users').select('referral_code').eq('id', row.id).maybeSingle();
    if (again?.referral_code) return again.referral_code;
  }
  return null;
}

/** Security definer RPC: rewards + referred_by */
export async function completeReferralWithCode(rawCode) {
  if (!isSupabaseConfigured) return { ok: false, reason: 'no_sb' };
  const sb = getSupabase();
  if (!sb) return { ok: false, reason: 'no_sb' };
  const code = (rawCode || '').trim();
  if (!code) return { ok: false, reason: 'empty' };
  const { error } = await sb.rpc('complete_referral', { p_code: code });
  if (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[referral]', error.message);
    }
    return { ok: false, reason: error.message };
  }
  return { ok: true };
}

export async function generateReferralCode(userId) {
  if (!isSupabaseConfigured || !userId) return { ok: false, error: 'invalid' };
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'no_sb' };
  const { data, error } = await sb.rpc('generate_referral_code', { p_user_id: userId });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data };
}

export async function processReferral(code, inviteeId) {
  if (!isSupabaseConfigured || !code || !inviteeId) return { ok: false, error: 'invalid' };
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'no_sb' };
  const { data, error } = await sb.rpc('process_referral', { p_code: String(code).trim(), p_invitee_id: inviteeId });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data };
}

export async function getMyReferralCode(userId) {
  if (!isSupabaseConfigured || !userId) return null;
  const sb = getSupabase();
  if (!sb) return null;
  const { data, error } = await sb.from('users').select('referral_code').eq('id', userId).maybeSingle();
  if (error || !data?.referral_code) return null;
  return { code: data.referral_code };
}

export async function getReferralStats(userId) {
  if (!isSupabaseConfigured || !userId) return { invitedCount: 0, rewardPoints: 0 };
  const sb = getSupabase();
  if (!sb) return { invitedCount: 0, rewardPoints: 0 };

  const { data: referrals } = await sb
    .from('referrals')
    .select('id, reward_given')
    .eq('referrer_id', userId);
  const invitedCount = Array.isArray(referrals) ? referrals.length : 0;
  const rewardPoints = (referrals || []).reduce((sum, row) => sum + (row?.reward_given ? 1 : 0), 0);
  return { invitedCount, rewardPoints };
}
